import React from 'react';

import i18n from 'util/i18n';

import { Summary, DateSummary } from 'components/Summary';

import { SUBSTANCE_USE, SUBSTANCE_USE_DRUGS_ORDERED } from 'config/formSections/substanceUse';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString } from 'helpers/text';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import ErrorMessage from 'components/ErrorMessage';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { drugsMandatoryTreatmentAddressNameFields } from 'constants/enums/people';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import OrderedTreatment from './OrderedTreatment';

const sectionConfig = {
  key: SUBSTANCE_USE_DRUGS_ORDERED.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_DRUGS_ORDERED.name,
  storeKey: SUBSTANCE_USE_DRUGS_ORDERED.storeKey,
};

export class OrderedTreatments extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateTreatmentOrdered = this.updateTreatmentOrdered.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      TreatmentOrdered: this.props.TreatmentOrdered,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateTreatmentOrdered = (values) => {
    this.update({
      TreatmentOrdered: values,
      List: values.value === 'Yes' ? this.props.List : [],
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {},
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const range = DateSummary(o.TreatmentDates);
    const explanation = (o.Explanation || {}).value;

    return Summary({
      type: i18n.t('substance.drugs.ordered.collection.itemType'),
      index,
      left: explanation,
      right: range,
      placeholder: i18n.t('substance.drugs.ordered.collection.summary'),
    });
  };

  render() {
    const {
      formType, errors, peopleWhoKnowYou, List
    } = this.props;
    const formTypeConfig = formType && formConfig[formType];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_TREATMENT_YEARS;

    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, drugsMandatoryTreatmentAddressNameFields);
    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    let branchLabelCopy;
    if (years === 'EVER') {
      branchLabelCopy = i18n.t('substance.drugs.heading.orderedTreatmentsEver');
    } else {
      const numberOfYearsString = getNumberOfYearsString(years);
      branchLabelCopy = i18n.t('substance.drugs.heading.orderedTreatments', { numberOfYearsString });
    }

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div
        className="section-content ordered-treatments"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_ORDERED.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.drugs.ordered')}</h1>
        <Branch
          name="TreatmentOrdered"
          label={i18n.t('substance.pvq.drugs.heading.orderedTreatmentsEver')}
          labelSize="h4"
          className="treatment-ordered"
          {...this.props.TreatmentOrdered}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateTreatmentOrdered}
          scrollIntoView={this.props.scrollIntoView}
          attribute="TreatmentOrdered"
        />
        <Show when={this.props.TreatmentOrdered.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.drugs.ordered.collection.description')}
            appendTitle={i18n.t('substance.pvq.drugs.ordered.collection.appendTitle')}
            appendLabel={i18n.t('substance.pvq.drugs.ordered.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <OrderedTreatment
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
              formType={this.props.formType}
            />
          </Accordion>
        </Show>
      </div>

    ) : (
      <div
        className="section-content ordered-treatments"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_ORDERED.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.drugs.ordered')}</h1>
        <Branch
          name="TreatmentOrdered"
          label={branchLabelCopy}
          labelSize="h4"
          className="treatment-ordered"
          {...this.props.TreatmentOrdered}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateTreatmentOrdered}
          scrollIntoView={this.props.scrollIntoView}
          attribute="TreatmentOrdered"
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        <Show when={this.props.TreatmentOrdered.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.drugs.ordered.collection.description')}
            appendTitle={i18n.t('substance.drugs.ordered.collection.appendTitle')}
            appendLabel={i18n.t('substance.drugs.ordered.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <OrderedTreatment
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

OrderedTreatments.defaultProps = {
  TreatmentOrdered: {},
  List: { items: [], branch: {} },
  Comments: {},
  SummaryComments: {},
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'drugs/ordered',
  addressBooks: {},
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
  formType: '',
};

export default connectSubsection(OrderedTreatments, sectionConfig);
